/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { ZoomUI } from "components/hud/ZoomUI";
import { inject } from "mobx-react";
import { isZoomFeatureEnabled, getShouldUseModernViewer } from "selectors/appSelectors";
import { isLiquidModeViewEnabled } from "selectors/liquidModeViewSelectors";
import { isMac } from "dc-preview-utils/src/Utils/DeviceUtils";
import { KEYBOARD_SHORTCUTS } from "constants/constants";
import { ZOOM_TRIGGER_TYPE } from "constants/enums";
import { providers } from "dc-core";

export const init = stores => () => {
  providers["keyboard-shortcuts-provider"]().then(shortcutManager => {
    const modKey = isMac() ? "Meta" : "Ctrl";
    shortcutManager.addShortcuts([
      {
        id: KEYBOARD_SHORTCUTS.ZOOM_IN,
        shortcutExprs: [`${modKey}+NumpadAdd`, `${modKey}+Equal`],
        handler: () => {
          stores.zoom.zoomIn(ZOOM_TRIGGER_TYPE.KEYBOARD);
          return { shouldPreventDefault: true };
        }
      },
      {
        id: KEYBOARD_SHORTCUTS.ZOOM_OUT,
        shortcutExprs: [`${modKey}+NumpadSubtract`, `${modKey}+Minus`],
        handler: () => {
          stores.zoom.zoomOut(ZOOM_TRIGGER_TYPE.KEYBOARD);
          return { shouldPreventDefault: true };
        }
      },
      {
        id: KEYBOARD_SHORTCUTS.RESET_ZOOM,
        shortcutExprs: [`${modKey}+0`, `${modKey}+Numpad0`],
        handler: () => {
          stores.zoom.resetZoom(ZOOM_TRIGGER_TYPE.KEYBOARD);
          return { shouldPreventDefault: true };
        }
      }
    ]);
  });
};

export const cleanup = stores => () => {
  providers["keyboard-shortcuts-provider"]().then(keyboardShortcutProvider => {
    const shortcutManager = keyboardShortcutProvider;
    if (shortcutManager)
      shortcutManager.removeShortcuts([
        KEYBOARD_SHORTCUTS.ZOOM_IN,
        KEYBOARD_SHORTCUTS.ZOOM_OUT,
        KEYBOARD_SHORTCUTS.RESET_ZOOM
      ]);
  });
};

const mapStoreToProps = stores => ({
  init: init(stores),
  cleanup: cleanup(stores),
  enableZoomFeature: isZoomFeatureEnabled(stores),
  enableLiquidModeFeature: isLiquidModeViewEnabled(stores),
  useModernViewer: getShouldUseModernViewer(stores)
});

const ZoomUIContainer = inject(mapStoreToProps)(ZoomUI);

export { ZoomUIContainer };
